/**
 * Calendar arithmetic on civil dates ("2026-10-14"), never on instants.
 *
 * The reschedule calendar is the CLINIC's calendar: the server keys its days in
 * the location's zone, and a day is a day there whatever clock the patient's
 * browser keeps. So the grid is built from the date strings themselves, with
 * UTC as a neutral arithmetic (no daylight-saving day is ever 23 or 25 hours
 * long here, and no browser zone moves a date across midnight).
 *
 * Weeks start on Sunday, the convention of the app's language (en-CA).
 */

import {APP_LOCALE} from "../../utils/appLocale";

const DAY_MS = 24 * 60 * 60 * 1000;
const pad = (n) => String(n).padStart(2, "0");

const toUtc = (key) => {
  const [year, month, day] = String(key).split("-").map(Number);
  return Date.UTC(year, month - 1, day);
};
const fromUtc = (ms) => {
  const date = new Date(ms);
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
};

/** "2026-10" of "2026-10-14". */
export const monthOf = (key) => String(key).slice(0, 7);

export const addDays = (key, days) => fromUtc(toUtc(key) + days * DAY_MS);

/** "2026-10" moved by whole months. */
export function addMonths(monthKey, months) {
  const [year, month] = String(monthKey).split("-").map(Number);
  const total = year * 12 + (month - 1) + months;
  return `${Math.floor(total / 12)}-${pad((total % 12) + 1)}`;
}

export function daysInMonth(monthKey) {
  const [year, month] = String(monthKey).split("-").map(Number);
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** The same day of another month, clamped to its length (Jan 31 → Feb 28). */
export function sameDayInMonth(key, months) {
  const day = Number(String(key).slice(8, 10));
  const month = addMonths(monthOf(key), months);
  return `${month}-${pad(Math.min(day, daysInMonth(month)))}`;
}

/** 0 for Sunday … 6 for Saturday. */
export const weekdayOf = (key) => new Date(toUtc(key)).getUTCDay();

export const startOfWeek = (key) => addDays(key, -weekdayOf(key));
export const endOfWeek = (key) => addDays(key, 6 - weekdayOf(key));

/** The month as weeks of seven; days outside it are null. */
export function monthWeeks(monthKey) {
  const first = `${monthKey}-01`;
  const cells = Array(weekdayOf(first)).fill(null);
  for (let day = 1; day <= daysInMonth(monthKey); day += 1) cells.push(`${monthKey}-${pad(day)}`);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let index = 0; index < cells.length; index += 7) weeks.push(cells.slice(index, index + 7));
  return weeks;
}

/** "October 2026". */
export function monthTitle(monthKey) {
  const [year, month] = String(monthKey).split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(APP_LOCALE, {month: "long", year: "numeric", timeZone: "UTC"});
}

/** "Wednesday, October 14". */
export function longDay(key) {
  return new Date(toUtc(key)).toLocaleDateString(APP_LOCALE, {weekday: "long", month: "long", day: "numeric", timeZone: "UTC"});
}

/** Sunday-first weekday names, short ("Sun") and long ("Sunday"). */
export const WEEKDAYS = Object.freeze(
  // 2026-10-04 is a Sunday.
  [0, 1, 2, 3, 4, 5, 6].map((offset) => {
    const at = toUtc("2026-10-04") + offset * DAY_MS;
    const name = (weekday) => new Date(at).toLocaleDateString(APP_LOCALE, {weekday, timeZone: "UTC"});
    return {short: name("short"), long: name("long")};
  })
);
